import React, { createContext, useContext, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { identify as analyticsIdentify, reset as analyticsReset, track as analyticsTrack, bootAnalytics } from "@/lib/analytics";
import { setUser as sentrySetUser } from "@/lib/sentry";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Boot PostHog (no-op until consent === "all")
    bootAnalytics();
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        analyticsIdentify(r.data);
        sentrySetUser(r.data);
      })
      .catch(() => localStorage.removeItem("token"))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password, turnstileToken, rememberMe = true) => {
    const { data } = await api.post("/auth/login", {
      email, password,
      turnstile_token: turnstileToken || undefined,
      remember_me: !!rememberMe,
    });
    localStorage.setItem("token", data.token);
    try {
      if (rememberMe) localStorage.setItem("remembered_email", email);
      else localStorage.removeItem("remembered_email");
    } catch { /* storage blocked — non-fatal */ }
    const u = { id: data.id, email: data.email, name: data.name, role: data.role };
    setUser(u);
    analyticsIdentify(u);
    sentrySetUser(u);
    analyticsTrack("auth:login_succeeded", { method: "password" });
    return data;
  };

  const register = async (email, password, name, turnstileToken, extra = {}) => {
    // Pull the anonymous id so any cookie-banner choice made BEFORE
    // this user signed up can be linked to their account in the
    // consents audit trail.
    let anonId = null;
    try { anonId = localStorage.getItem("anon_id"); } catch (_e) { /* private mode */ }
    // Breeder-onboarding referral code captured by <ReferralCapture/>
    // at the app root. Forward it so the new user's row records who
    // referred them and the referrer's `uses` counter ticks.
    let referralCode = null;
    try { referralCode = localStorage.getItem("armstrong_ref_code") || null; } catch (_e) { /* private mode */ }
    let signupSource = null;
    try {
      const { getSignupSource } = await import("@/lib/signupSource");
      signupSource = getSignupSource();
    } catch (_e) { /* private mode */ }
    const { data } = await api.post("/auth/register", {
      email, password, name,
      turnstile_token: turnstileToken || undefined,
      anon_id: anonId || undefined,
      referral_code: referralCode || undefined,
      signup_source: signupSource || undefined,
      // Hard-coded `true` because the Register form already gates the
      // submit button on the visible checkbox. Sending the flag makes
      // the backend's audit row a primary record instead of an
      // implicit one — required by FTC ROSCA / CA ARL.
      accepted_terms: true,
      ...extra,
    });
    localStorage.setItem("token", data.token);
    const u = { id: data.id, email: data.email, name: data.name, role: data.role };
    setUser(u);
    analyticsIdentify(u);
    sentrySetUser(u);
    analyticsTrack("auth:signup_completed", { method: "password" });
    // Record explicit ToS + Privacy acceptance — every signup implies
    // these (the form text says "By creating an account, you agree…").
    // Fire-and-forget.
    try {
      api.post("/consents/record", { kind: "terms", value: "accepted", origin: "signup", anon_id: anonId || undefined }).catch(() => {});
      api.post("/consents/record", { kind: "privacy", value: "accepted", origin: "signup", anon_id: anonId || undefined }).catch(() => {});
    } catch { /* never block signup */ }
    return data;
  };

  const setUserFromGoogle = (userData) => {
    setUser(userData);
    analyticsIdentify(userData);
    sentrySetUser(userData);
    analyticsTrack("auth:login_succeeded", { method: "google" });
  };

  // Used by /reset-password — accepts the same shape the backend returns
  // for login/register (id, email, name, role, token) and persists both.
  const setUserAndToken = (userData, token) => {
    if (token) localStorage.setItem("token", token);
    const u = {
      id: userData.id,
      email: userData.email,
      name: userData.name,
      role: userData.role,
    };
    setUser(u);
    analyticsIdentify(u);
    sentrySetUser(u);
    analyticsTrack("auth:password_reset_completed");
  };

  const logout = () => {
    analyticsTrack("auth:logout");
    // Best-effort: stop push notifications for this account on this
    // device. keepalive lets the request survive the hard navigation.
    try {
      const authTok = localStorage.getItem("token");
      const pushTok = localStorage.getItem("native_push_token");
      if (authTok && pushTok) {
        fetch(`${API}/push/register-token`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authTok}` },
          body: JSON.stringify({ token: pushTok }),
          keepalive: true,
        }).catch(() => {});
        localStorage.removeItem("native_push_token");
      }
    } catch {}
    // NOTE: the Face ID keychain session is intentionally KEPT on
    // sign-out — it is protected by the device biometric itself and is
    // what makes the "Sign in with Face ID" button appear next time.
    // It is cleared only on account deletion.
    localStorage.removeItem("token");
    setUser(null);
    analyticsReset();
    sentrySetUser(null);
    // Best-effort server-side session clear — fired before the hard
    // navigation kicks the page so the inflight request actually leaves
    // the browser.
    api.post("/auth/logout-full").catch(() => {});
    // Hard navigation to the landing page.
    //
    // We deliberately do NOT use react-router's `navigate("/")` here
    // because of a real, observable race: when the user is on a
    // <ProtectedRoute>-wrapped page (e.g. /dashboard) and we call
    // setUser(null) + navigate("/") in the same tick, the still-mounted
    // ProtectedRoute renders <Navigate to="/login" replace /> the
    // instant it sees user===null, which `replace`-mutates history to
    // /login before our imperative navigate("/") lands. End result:
    // the user logged out lands on /login instead of "/".
    //
    // window.location.replace("/") flushes all in-memory router state,
    // forces a fresh boot, and lands the user on the public landing
    // page every single time — exactly what users expect on sign-out.
    window.location.replace("/");
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUserFromGoogle, setUserAndToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
